import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Unlock, Upload } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const emptyProd = { name: "", price: "", category: "Épicerie", unit: "pièce", image: "", stock: "" };
const STATUSES = [["confirmed", "Confirmée"], ["preparing", "En préparation"], ["ready", "Prête"], ["collected", "Retirée"], ["cancelled", "Annulée"]];

export function ShopAdmin() {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(emptyProd);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const [s, p, o] = await Promise.all([api.get("/settings"), api.get("/shop/products", { params: { all: true } }), api.get("/shop/orders")]);
    setSettings(s.data); setProducts(p.data); setOrders(o.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveShop = async (shop) => {
    try {
      const { data } = await api.put("/settings", { shop });
      setSettings(data);
      toast.success(data.shop_open ? "Drive ouvert" : "Drive fermé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addProduct = async () => {
    try {
      await api.post("/shop/products", { ...form, price: Number(form.price), stock: form.stock === "" ? null : Number(form.stock) });
      toast.success("Produit ajouté"); setForm(emptyProd); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleAvail = async (p) => {
    const { product_id, created_at, ...rest } = p;
    await api.put(`/shop/products/${product_id}`, { ...rest, available: !p.available }); load();
  };

  const removeProduct = async (id) => { await api.delete(`/shop/products/${id}`); load(); };

  const setStock = async (p, value) => {
    const { product_id, created_at, ...rest } = p;
    const stock = value === "" ? null : Math.max(0, Number(value));
    if (stock === (p.stock ?? null)) return;
    await api.put(`/shop/products/${product_id}`, { ...rest, stock }); load();
  };

  const uploadImg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image: BACKEND + data.url }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const setStatus = async (id, status) => {
    if (status === "cancelled" && !window.confirm("Annuler cette commande ? Si elle est payée, le client sera remboursé via Stripe.")) return;
    const { data } = await api.patch(`/shop/orders/${id}/status`, null, { params: { status } });
    if (data.refund === "refunded") toast.success("Commande annulée · client remboursé via Stripe");
    else if (data.refund === "error") toast.error("Commande annulée, mais le remboursement Stripe a échoué");
    load();
  };

  if (!settings) return null;
  const shop = settings.shop;
  const updSlot = (i, k, v) => saveShop({ ...shop, slots: shop.slots.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });

  return (
    <div className="space-y-8" data-testid="shop-admin">
      <div className="rounded-2xl bg-white border border-border p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-serif text-xl text-olive">Horaires du drive</h3>
            <p className="text-sm text-muted-foreground">Statut actuel : <span className={settings.shop_open ? "text-olive font-semibold" : "text-rose-brand font-semibold"} data-testid="admin-shop-status">{settings.shop_open ? "Ouvert" : "Fermé"}</span> · {settings.now_paris} (Paris)</p>
          </div>
          <Button onClick={() => saveShop({ ...shop, force_closed: !shop.force_closed })} variant={shop.force_closed ? "default" : "outline"} className={shop.force_closed ? "bg-olive text-white" : "border-rose-brand text-rose-brand"} data-testid="shop-force-toggle">
            {shop.force_closed ? <><Unlock className="w-4 h-4 mr-2" /> Rouvrir le drive</> : <><Lock className="w-4 h-4 mr-2" /> Fermer maintenant</>}
          </Button>
        </div>
        <div className="mt-4 space-y-2">
          {shop.slots.map((s, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`shop-slot-${i}`}>
              <Input type="time" value={s.start} onChange={(e) => updSlot(i, "start", e.target.value)} className="w-32" data-testid={`slot-start-${i}`} />
              <span className="text-muted-foreground">→</span>
              <Input type="time" value={s.end} onChange={(e) => updSlot(i, "end", e.target.value)} className="w-32" data-testid={`slot-end-${i}`} />
              <button onClick={() => saveShop({ ...shop, slots: shop.slots.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive" data-testid={`slot-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <Button variant="ghost" size="sm" onClick={() => saveShop({ ...shop, slots: [...shop.slots, { start: "14:00", end: "18:00" }] })} className="text-olive" data-testid="slot-add"><Plus className="w-4 h-4 mr-1" /> Ajouter un créneau</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {DAYS.map((d, i) => (
            <button key={d} onClick={() => saveShop({ ...shop, days: shop.days.includes(i) ? shop.days.filter((x) => x !== i) : [...shop.days, i].sort() })}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${shop.days.includes(i) ? "bg-olive text-white border-olive" : "bg-white text-muted-foreground border-border"}`} data-testid={`shop-day-${i}`}>{d}</button>
          ))}
        </div>
        <div className="mt-3">
          <Label className="text-xs">Message affiché aux clients</Label>
          <Input defaultValue={shop.message} onBlur={(e) => e.target.value !== shop.message && saveShop({ ...shop, message: e.target.value })} placeholder="Ex : Fermé exceptionnellement le 15 août" data-testid="shop-message-input" />
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-border p-5">
        <h3 className="font-serif text-xl text-olive mb-3">Produits</h3>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
          <div className="col-span-2"><Label className="text-xs">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
          <div><Label className="text-xs">Prix €</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" /></div>
          <div><Label className="text-xs">Catégorie</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category-input" /></div>
          <div><Label className="text-xs">Unité</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
          <div><Label className="text-xs">Stock (vide = illimité)</Label><Input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock-input" /></div>
          <label className="h-10 flex items-center justify-center gap-1 rounded-md border border-dashed border-olive text-olive text-xs cursor-pointer">
            <Upload className="w-3.5 h-3.5" /> {uploading ? "..." : form.image ? "Photo ✓" : "Photo"}
            <input type="file" accept="image/*" onChange={uploadImg} className="hidden" data-testid="product-image-input" />
          </label>
        </div>
        <Button onClick={addProduct} disabled={!form.name || !form.price} className="mt-3 bg-rose-brand hover:bg-rosebrand-soft text-white" data-testid="product-add-button"><Plus className="w-4 h-4 mr-1" /> Ajouter le produit</Button>

        <div className="mt-4 divide-y divide-border" data-testid="product-list">
          {products.map((p) => (
            <div key={p.product_id} className="flex items-center gap-3 py-2" data-testid={`admin-product-${p.product_id}`}>
              {p.image ? <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg bg-secondary" />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-olive truncate">{p.name} <span className="text-xs text-muted-foreground font-normal">· {p.category}</span></p>
                <p className="text-sm text-muted-foreground">{p.price.toFixed(2)} € / {p.unit}
                  {p.stock === 0 && <span className="ml-2 text-xs font-semibold text-rose-brand" data-testid={`product-soldout-${p.product_id}`}>Épuisé</span>}
                </p>
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <span>Stock</span>
                <Input type="number" min="0" defaultValue={p.stock ?? ""} placeholder="∞" onBlur={(e) => setStock(p, e.target.value)} className="w-20 h-8 text-xs" data-testid={`product-stock-${p.product_id}`} />
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{p.available ? "Dispo" : "Masqué"}</span>
                <Switch checked={p.available} onCheckedChange={() => toggleAvail(p)} data-testid={`product-toggle-${p.product_id}`} />
              </div>
              <button onClick={() => removeProduct(p.product_id)} className="text-muted-foreground hover:text-destructive" data-testid={`product-delete-${p.product_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-border p-5">
        <h3 className="font-serif text-xl text-olive mb-3">Commandes drive</h3>
        {orders.length === 0 && <p className="text-sm text-muted-foreground">Aucune commande.</p>}
        <div className="space-y-2" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.order_id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border flex-wrap" data-testid={`admin-order-${o.order_id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-olive">{o.user_name} <span className="text-muted-foreground font-normal text-sm">· {o.listing_title || "sans logement"}</span></p>
                <p className="text-xs text-muted-foreground truncate">{o.pickup_time ? `🕒 ${o.pickup_time} · ` : ""}{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}{o.note ? ` — “${o.note}”` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-olive">{o.total.toFixed(2)} €</span>
                <Badge className={o.payment_status === "refunded" ? "bg-secondary text-olive" : o.payment_status === "paid" ? "bg-olive text-white" : "bg-rosebrand-pale text-rose-brand"}>{o.payment_status === "refunded" ? "Remboursée" : o.payment_status === "paid" ? "Payée" : "Non payée"}</Badge>
                <Select value={o.status} onValueChange={(v) => setStatus(o.order_id, v)}>
                  <SelectTrigger className="w-40 h-8 text-xs" data-testid={`order-status-${o.order_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {o.status === "pending" && <SelectItem value="pending">En attente</SelectItem>}
                    {STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
